import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContentCard from '@/components/feature/ContentCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles.filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug).slice(0, 4)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 flex items-center justify-center mb-4">
            <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要查看的内容不存在或已下架</p>
          <Link
            to="/"
            className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '香蕉影视',
    },
    publisher: {
      '@type': 'Organization',
      name: '香蕉影视',
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(seoJsonLd) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <article className="w-full px-4 md:px-6 py-8 md:py-12 max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="aspect-[2/3] rounded-lg overflow-hidden sticky top-24">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md whitespace-nowrap">
                    {article.category}
                  </span>
                  <div className="flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-primary-500"></i>
                    <span className="font-semibold text-foreground-800">{article.rating}</span>
                  </div>
                  <span className="text-sm text-foreground-500">{article.year}</span>
                  <span className="text-sm text-foreground-500">{article.duration}</span>
                </div>

                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                  <strong>{article.description}</strong>
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-background-100 rounded-lg">
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">类型</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.genre.join(' / ')}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-foreground-500">主演</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.cast.join(' / ')}</p>
                  </div>
                </div>

                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex flex-wrap items-center gap-4 mt-8 pt-6 border-t border-background-200/70">
                  <span className="text-xs text-foreground-500">发布于 {article.publishDate}</span>
                  <span className="text-xs text-foreground-500">{article.views.toLocaleString()} 次浏览</span>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {relatedArticles.map((a) => (
                  <ContentCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}